import {
  Accessor,
  createEffect,
  createMemo,
  createSignal,
  JSX,
  on,
  onCleanup,
} from 'solid-js';
import { t } from '@trimble/i18n';

import { PointCloudStylingContainer } from '@trimble/point-cloud-styling';
import {
  FeetInchUnit,
  PointCloudStyling,
  PointCloudStylingType,
  PointCloudStylingUOM,
} from '@trimble/types';
import { PanelLayout } from '@tcweb/connect-ui';
import { PointCloudStylingCapability } from '@trimble/web3d-capabilities';
import { PointCloudStylingCapabilityProvider } from '@trimble/web3d-providers';
import { useProjectSettings } from '@mobile-mapping/providers';
import { convertLength } from '@trimble/utilities';

export const StylesTab = (props: {
  pointCloudStylingProvider: PointCloudStylingCapabilityProvider;
  pointCloudStylingCapability: PointCloudStylingCapability;
  isVisible: Accessor<boolean>;
}): JSX.Element => {
  const { formattingSettings } = useProjectSettings();

  const uom = createMemo((): PointCloudStylingUOM => {
    const formatting = formattingSettings();
    const feetInches = formatting.lengthUnit.toString() === FeetInchUnit;
    return {
      feetInches,
      unitLabel: formatting.lengthUnit,
      lengthFractions: formatting.lengthFractions,
      lengthDecimals: formatting.lengthDecimals,
      meterToUnitFactor: feetInches
        ? 1
        : convertLength(1, 'm', formatting.lengthUnit),
    };
  });

  const [elevationPicking, setElevationPicking] = createSignal<boolean>(false);

  const styleType = createMemo(() => {
    return props.pointCloudStylingProvider.pointCloudStyling().stylingType;
  });

  createEffect(
    on(styleType, (type) => {
      const pointCloudStyling =
        props.pointCloudStylingProvider.pointCloudStyling();

      if (
        type === PointCloudStylingType.Elevation &&
        pointCloudStyling.elevation?.reference === undefined
      ) {
        setElevationPicking(true);
      } else {
        setElevationPicking(false);
      }
    })
  );

  createEffect(() => {
    if (props.isVisible()) {
      props.pointCloudStylingCapability.activate();
    } else {
      props.pointCloudStylingCapability.deactivate();
    }
  });

  onCleanup(() => {
    props.pointCloudStylingCapability.deactivate();
  });

  createEffect(
    on(
      () => props.pointCloudStylingCapability.elevationPicking(),
      (picking) => {
        if (!picking) {
          setElevationPicking(false);
        }
      }
    )
  );

  createEffect(() => {
    if (!props.isVisible()) return;

    props.pointCloudStylingCapability.toggleElevationPicking(
      elevationPicking()
    );
  });

  const onStylingChange = (styling: PointCloudStyling): void => {
    props.pointCloudStylingProvider.setPointCloudStyling(styling);
  };

  const onElevationPickingToggle = (elevationPicking: boolean): void => {
    setElevationPicking(elevationPicking);
  };

  return (
    <PanelLayout
      panelTitle={t('mobileMapping.styles.header')}
      content={
        <div class="p-3">
          <PointCloudStylingContainer
            classifications={props.pointCloudStylingProvider.classifications()}
            styling={props.pointCloudStylingProvider.pointCloudStyling()}
            onStylingChange={onStylingChange}
            elevationPicked={props.pointCloudStylingCapability.elevationPicked()}
            elevationPicking={elevationPicking()}
            uom={uom()}
            onElevationPickingToggle={onElevationPickingToggle}
          />
        </div>
      }
    />
  );
};
